/**
 * BePillarPage — French-first pillar template for the 4 Belgian
 * litigation landing pages (BP §14.2, chantier "seo-content").
 *
 * Distinct from the legacy PillarPage.jsx which serves the US pillar
 * pages: this template speaks French to the reader (tutoiement — same
 * voice as the product), never quotes an unverified figure, and its
 * final CTA sends the visitor to the free analysis (not a Stripe checkout).
 * Everything after "Ce qu'Archer fait pour toi" honours the honesty
 * rule laid out in content/bePillarPages.js.
 *
 * Content lives in `content/bePillarPages.js`. Metadata lives in
 * `lib/seo/metadata.js` (BE_PILLAR_METADATA is nested under the
 * `PAGE_METADATA` catalog). Both look up the same slug.
 */
import React, { useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PageHead from '../components/seo/PageHead';
import JsonLd, { createFaqSchema, createBreadcrumbSchema } from '../components/seo/JsonLd';
import PublicHeader from '../components/PublicHeader';
import SiteFooterV2 from '../components/siteV2/SiteFooterV2';
import { BE_PILLAR_CONTENT } from '../content/bePillarPages';
import { PAGES_DETRESSE } from '../content/pagesDetresse';
import { getPageMetadata, SITE_URL } from '../lib/seo/metadata';
import { ROUTE_START } from '../components/siteV2/routes';
import useStartRoute from '../hooks/useStartRoute';
import { useAuth } from '../contexts/AuthContext';


/**
 * Destination des deux CTA « Scanner mon document · gratuit ».
 *
 * Elle pointait `/upload`, qui est sous `ProtectedRoute` : un visiteur
 * venu de Google cliquait un bouton marqué « gratuit » et recevait un
 * écran de connexion. Mesuré le 16/08/2026 sur la production —
 * /contester-amende-radar → /login.
 *
 * Le parcours qui marche est celui des landings thématiques
 * (LandingThemePage.jsx, `startAnalysis`) : connecté → /upload,
 * sinon inscription PUIS /upload, porté par `?next=`.
 *
 * Ici on garde un vrai `<Link>` plutôt qu'un bouton : ces quatre pages
 * n'existent que pour être explorées, et le href doit rester suivable.
 * L'attribut porte donc la cible du visiteur ANONYME — la majorité, et
 * ce que voit un robot — et le `onClick` ne fait que court-circuiter
 * vers /upload quand `user` est résolu.
 *
 * DEPUIS LE DÉPÔT SANS INSCRIPTION, la cible anonyme est `/analyser` :
 * le bouton dit « gratuit », et il mène enfin à un écran où l'on dépose
 * un document au lieu d'un écran où l'on crée un compte. La constante
 * ci-dessous reste le repli tant que les drapeaux n'ont pas répondu, et
 * la destination définitive si le parcours est refermé.
 */
const CTA_HREF_FALLBACK = `${ROUTE_START}?next=/upload`;


// Render `content` (a string) by splitting on double newlines into
// paragraphs. Keeps the template dumb — the source of truth for line
// breaks stays in the content file.
function Paragraphs({ text }) {
  if (!text) return null;
  const parts = String(text).split(/\n\n+/);
  return parts.map((p, i) => (
    <p
      key={i}
      style={{
        margin: i === 0 ? '0 0 12px' : '0 0 12px',
        fontSize: 16,
        color: '#374151',
        lineHeight: 1.8,
      }}
    >
      {p}
    </p>
  ));
}


export default function BePillarPage() {
  const { pillarSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Cf. CTA_HREF_FALLBACK plus haut. Tant que `user` est nul — anonyme,
  // ou auth encore en cours — on laisse le lien faire son travail vers
  // la porte publique du moment.
  const startRoute = useStartRoute();
  const ctaHref = startRoute === ROUTE_START ? CTA_HREF_FALLBACK : startRoute;
  const startAnalysis = useCallback((e) => {
    if (!user) return;
    e.preventDefault();
    navigate('/upload');
  }, [user, navigate]);

  // The route can also match on a fixed literal path (e.g. registered
  // as <Route path="/contester-amende-radar" .../>). In that case the
  // param is undefined; fall back to the raw pathname's first segment.
  const slug = pillarSlug || window.location.pathname.split('/').filter(Boolean)[0];
  // Les pages de détresse (content/pagesDetresse.js) partagent ce gabarit ;
  // elles ajoutent `sources` par section, `updatedAt` et `reviewStatus`.
  const pillar = BE_PILLAR_CONTENT[slug] || PAGES_DETRESSE[slug];

  if (!pillar) {
    return (
      <div style={{ padding: 80, textAlign: 'center', color: '#6b7280' }}>
        Page introuvable. <Link to="/">Retour à l’accueil</Link>
      </div>
    );
  }

  const meta = getPageMetadata(slug, 'fr');
  const canonical = meta?.canonical || `${SITE_URL}/${slug}`;

  return (
    <div style={{ minHeight: '100vh', background: '#fafaf8' }}>
      <PageHead metadata={meta} />
      {/* FAQ schema — emitted only when the page ships FAQs so we never
          declare an empty FAQPage. */}
      {pillar.faqs?.length > 0 && (
        <JsonLd data={createFaqSchema(
          pillar.faqs.map((f) => ({ question: f.question, answer: f.answer })),
        )} />
      )}
      <JsonLd data={createBreadcrumbSchema([
        { name: 'Archer', url: SITE_URL },
        { name: 'Guides juridiques', url: `${SITE_URL}/guides-juridiques` },
        { name: pillar.title, url: canonical },
      ])} />

      <PublicHeader />

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '60px 24px 40px' }}>
        {/* Breadcrumb — same style the legacy template uses, just in FR. */}
        <nav style={{ fontSize: 12, color: '#9ca3af', marginBottom: 24, display: 'flex', gap: 6 }}>
          <Link to="/" style={{ color: '#9ca3af', textDecoration: 'none' }}>Accueil</Link>
          <span>/</span>
          <span style={{ color: '#6b7280' }}>{pillar.title}</span>
        </nav>

        {/* Hero — H1 + empathic intro + primary CTA. Exactly one <h1>
            per page (SEO rule from the chantier). */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{
            fontSize: 40, fontWeight: 800, color: '#0a0a0f',
            letterSpacing: -1.5, lineHeight: 1.1, marginBottom: 16,
          }}>
            {pillar.heroTitle}
          </h1>
          <p style={{ fontSize: 18, color: '#6b7280', lineHeight: 1.6, marginBottom: 16 }}>
            {pillar.heroSubtitle}
          </p>
          {pillar.updatedAt && (
            <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }} data-testid="be-pillar-updated-at">
              À jour au {pillar.updatedAt}. Chaque section renvoie au texte de loi qui la fonde.
            </p>
          )}
          {pillar.reviewStatus === 'pending' && (
            <p style={{
              fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d',
              borderRadius: 8, padding: '8px 12px', marginBottom: 12,
            }} data-testid="be-pillar-review-pending">
              Page en relecture juridique : non référencée tant qu’elle n’est pas signée.
            </p>
          )}
          {pillar.intro && (
            <p style={{ fontSize: 15, color: '#374151', lineHeight: 1.7, marginBottom: 24 }}>
              {pillar.intro}
            </p>
          )}
          <Link to={ctaHref} onClick={startAnalysis} style={{
            display: 'inline-block', padding: '14px 28px',
            background: '#1a56db', color: '#fff', borderRadius: 10,
            fontSize: 15, fontWeight: 700, textDecoration: 'none',
          }} data-testid="be-pillar-hero-cta">
            Scanner mon document · gratuit
          </Link>
        </div>

        {/* Main content sections */}
        {pillar.sections?.map((section, i) => (
          <div key={i} style={{ marginBottom: 32 }}>
            <h2 style={{
              fontSize: 22, fontWeight: 800, color: '#0a0a0f',
              marginBottom: 10, lineHeight: 1.3,
            }}>
              {section.title}
            </h2>
            <Paragraphs text={section.content} />
            {section.sources?.length > 0 && (
              <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.7, margin: '4px 0 0' }} data-testid="be-pillar-sources">
                <strong style={{ color: '#4b5563' }}>Sources :</strong>{' '}
                {section.sources.map((s, j) => (
                  <span key={s.url + j}>
                    {j > 0 && ' · '}
                    <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1a56db' }}>{s.label}</a>
                  </span>
                ))}
              </p>
            )}
          </div>
        ))}

        {/* FAQ */}
        {pillar.faqs?.length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <h2 style={{
              fontSize: 22, fontWeight: 800, color: '#0a0a0f', marginBottom: 14,
            }}>
              Questions fréquentes
            </h2>
            {pillar.faqs.map((faq, i) => (
              <details key={i} style={{
                marginBottom: 8, background: '#fff', borderRadius: 10,
                border: '0.5px solid #e2e0db', overflow: 'hidden',
              }}>
                <summary style={{
                  padding: '14px 18px', fontSize: 14, fontWeight: 700,
                  color: '#0a0a0f', cursor: 'pointer', listStyle: 'none',
                }}>
                  {faq.question}
                </summary>
                <div style={{
                  padding: '0 18px 14px', fontSize: 14,
                  color: '#374151', lineHeight: 1.7,
                }}>
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        )}

        {/* CTA card — honest copy, no numbers we can't back. */}
        <div style={{
          padding: '28px 32px',
          background: 'linear-gradient(135deg, #eff6ff, #f0fdf4)',
          borderRadius: 14, border: '1px solid #1a56db',
          textAlign: 'center', marginBottom: 32,
        }}>
          <div style={{
            fontSize: 20, fontWeight: 800, color: '#0a0a0f', marginBottom: 8,
          }}>
            {pillar.ctaHeadline || 'Prêt à réagir ?'}
          </div>
          {pillar.ctaBody && (
            <div style={{ fontSize: 14, color: '#4b5563', marginBottom: 16, lineHeight: 1.6 }}>
              {pillar.ctaBody}
            </div>
          )}
          <Link to={ctaHref} onClick={startAnalysis} style={{
            display: 'inline-block', padding: '12px 24px',
            background: '#1a56db', color: '#fff',
            borderRadius: 10, fontSize: 14, fontWeight: 700,
            textDecoration: 'none',
          }} data-testid="be-pillar-final-cta">
            Scanner mon document · gratuit
          </Link>
        </div>

        {/* Cross-guide links — every pillar page shows the 3 others so
            the internal graph stays tight for both users and crawlers. */}
        <div style={{ marginBottom: 32 }}>
          <div style={{
            fontSize: 11, fontWeight: 800, color: '#9ca3af',
            letterSpacing: 0.8, marginBottom: 10,
          }}>
            AUTRES GUIDES JURIDIQUES
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Object.values(BE_PILLAR_CONTENT)
              .filter((p) => p.slug !== slug)
              .map((p) => (
                <Link
                  key={p.slug}
                  to={`/${p.slug}`}
                  style={{
                    padding: '10px 14px',
                    background: '#fff',
                    border: '0.5px solid #e2e0db',
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#1a56db',
                    textDecoration: 'none',
                  }}
                  data-testid={`be-pillar-crosslink-${p.slug}`}
                >
                  {p.title} →
                </Link>
              ))}
          </div>
        </div>

        {/* Disclaimer — honest, in French. Does not overpromise. */}
        <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.6 }}>
          <strong>Note :</strong> Cette page fournit une information juridique générale
          à titre indicatif. Elle ne constitue ni une consultation juridique, ni un avis
          personnalisé. Les règles varient selon les faits, les régions et les évolutions
          législatives. Pour une situation précise, tu peux passer par Archer (analyse
          gratuite) ou consulter un avocat.
        </div>
      </div>

      <SiteFooterV2 />
    </div>
  );
}
